// FT-084 STEP-11 (task-11-brief.md): клиентский модуль для POST /api/v1/price_quotes.
//
// Не компонент — чистый JS, тестируемый вне Vue (Mocha, `yarn test:unit`).
// Отвечает за:
//   1. identity квоты (buildQuoteIdentity/CTR-01 входы) — сравнение "актуальна
//      ли показанная квота для текущих pricing-входов формы";
//   2. debounce + guard от stale-ответов (BookingQuoteRequester) — если
//      пришёл ответ на запрос, который уже не последний отправленный,
//      он молча отбрасывается (не зовёт onSuccess/onError).
//
// CTR-01 (task-11-brief.md, §Модуль квоты): car_id, start_at/end_at строками
// "DD-MM-YYYY HH:mm" (wall-clock, БЕЗ TZ-сдвига), rental_type, sale/sale_cash/
// rental_cost_sale/rental_cost_sale_cash, tariff {sum,percent,id}, gifts,
// услуги-passthrough (delivery, delivery_end, equipment, insurance,
// clean_payment, add_drivers_cost, other, deposit), price_hour override.

// Пути, включённые в identity квоты (порядок фиксирован — детерминированный
// JSON.stringify для сравнения). Namespacing "services.*" — плоские поля,
// сериализуются в объект services при отправке (buildQuotePayload).
const SERVICE_KEYS = [
  "delivery",
  "delivery_end",
  "equipment",
  "insurance",
  "clean_payment",
  "add_drivers_cost",
  "other",
];

/**
 * Строит нормализованный identity-объект квоты из входа формы. Два вызова с
 * одинаковыми pricing-входами дают глубоко равные объекты (JSON.stringify
 * совпадает) — используется и для payload запроса, и для "квота ещё
 * актуальна?" сравнения.
 *
 * @param {object} params
 * @param {number|string} params.carId
 * @param {string} params.startAt - "DD-MM-YYYY HH:mm"
 * @param {string} params.endAt - "DD-MM-YYYY HH:mm"
 * @param {string} params.rentalType - daily|hourly|weekly|monthly
 * @param {number} [params.sale]
 * @param {number} [params.saleCash]
 * @param {number} [params.rentalCostSale]
 * @param {number} [params.rentalCostSaleCash]
 * @param {{sum?: number, percent?: number, id?: number}|null} [params.tariff]
 * @param {Array} [params.gifts]
 * @param {object} [params.services] - { delivery, delivery_end, equipment, insurance, clean_payment, add_drivers_cost, other, deposit }
 * @param {number|string|null} [params.priceHour]
 * @param {number|string|null} [params.manualPrice]
 * @param {number|string|null} [params.bookingId]
 */
export function buildQuoteIdentity(params) {
  const services = params.services || {};

  return {
    car_id: params.carId != null ? String(params.carId) : null,
    start_at: params.startAt || null,
    end_at: params.endAt || null,
    rental_type: params.rentalType || null,
    booking_id: params.bookingId != null ? String(params.bookingId) : null,
    sale: numOrZero(params.sale),
    sale_cash: numOrZero(params.saleCash),
    rental_cost_sale: numOrZero(params.rentalCostSale),
    rental_cost_sale_cash: numOrZero(params.rentalCostSaleCash),
    tariff: normalizeTariff(params.tariff),
    gifts: params.gifts && params.gifts.length ? params.gifts : [],
    services: SERVICE_KEYS.reduce((acc, key) => {
      acc[key] = numOrZero(services[key]);
      return acc;
    }, {}),
    deposit: services.deposit != null ? numOrZero(services.deposit) : null,
    price_hour: params.priceHour != null && params.priceHour !== "" ? String(params.priceHour) : null,
    manual_price: params.manualPrice != null && params.manualPrice !== "" ? String(params.manualPrice) : null,
  };
}

function numOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeTariff(tariff) {
  if (!tariff) return null;
  const sum = numOrZero(tariff.sum);
  const percent = numOrZero(tariff.percent);
  if (!sum && !percent) return null;
  return { sum, percent, id: tariff.id != null ? tariff.id : null };
}

/** Два identity-объекта соответствуют одному и тому же pricing-входу. */
export function quoteIdentityEquals(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

// Даты line items (starts_on/ends_on) приходят из CTR-02 ответа как ISO
// "YYYY-MM-DD"; UI показывает их как "DD-MM-YYYY" (manual QA 2026-07-13,
// блок «Сезоны» в формах брони). Чистый разворот строки — без Date/moment,
// чтобы не ловить TZ-сдвиг на полуночных датах.
export function formatQuoteDate(isoDate) {
  if (typeof isoDate !== "string") return isoDate;
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : isoDate;
}

// Режим определения сезона компании (companies.season_start_calc/
// season_end_calc, семантика Pricing::Engine#attributed_season_id):
// both = по-дневная атрибуция, start/end = сезон даты начала/конца на весь
// интервал, off = только основные цены. Ключ для i18n
// bookings.season_mode_<key> в панели «Сезоны» (manual QA 2026-07-13).
export function seasonModeKey(company) {
  if (!company) return null;
  const start = !!company.season_start_calc;
  const end = !!company.season_end_calc;
  if (start && end) return "both";
  if (start) return "start";
  if (end) return "end";
  return "off";
}

// CTR-01 JSON payload для POST /api/v1/price_quotes / priced_bookings из
// identity-объекта (buildQuoteIdentity). price_source намеренно не incl.
// здесь — caller передаёт его отдельно (create всегда "current", update может
// явно выбрать "stored").
export function buildQuotePayload(identity, extra = {}) {
  return {
    car_id: identity.car_id,
    start_at: identity.start_at,
    end_at: identity.end_at,
    rental_type: identity.rental_type,
    booking_id: identity.booking_id || null,
    sale: identity.sale,
    sale_cash: identity.sale_cash,
    rental_cost_sale: identity.rental_cost_sale,
    rental_cost_sale_cash: identity.rental_cost_sale_cash,
    tariff: identity.tariff,
    gifts: identity.gifts,
    services: identity.services,
    deposit: identity.deposit,
    price_hour: identity.price_hour,
    manual_price: identity.manual_price,
    ...extra,
  };
}

/**
 * Debounce + stale-response guard поверх POST /api/v1/price_quotes.
 *
 * Использование (компонент):
 *   const requester = new BookingQuoteRequester({
 *     post: (payload) => this.axios.post('/api/v1/price_quotes', payload, { headers }),
 *     debounceMs: 300,
 *   });
 *   requester.request(identity).then(quote => ...).catch(err => ...)
 *
 * Гарантия (task-11-brief.md §1): применяется ТОЛЬКО ответ, соответствующий
 * ПОСЛЕДНЕМУ отправленному запросу. Если ответ на запрос N приходит после
 * того, как уже был отправлен запрос N+1, промис запроса N никогда не
 * резолвится/реджектится (он "зависает" молча) — вызывающий код видит
 * только исход самого свежего запроса.
 */
export class BookingQuoteRequester {
  constructor({ post, debounceMs = 300 } = {}) {
    if (typeof post !== "function") {
      throw new Error("BookingQuoteRequester requires a `post` function");
    }
    this._post = post;
    this._debounceMs = debounceMs;
    this._seq = 0;
    this._timer = null;
  }

  /**
   * Запланировать (debounce) запрос квоты по identity. Возвращает промис,
   * который резолвится/реджектится ТОЛЬКО если к моменту получения ответа
   * этот запрос всё ещё последний (иначе промис не завершается никогда —
   * caller должен полагаться на актуальный вызов, не на await предыдущего).
   *
   * @param {object} identity - buildQuoteIdentity() output
   * @param {object} [extra] - FT-084 STEP-12: extra top-level payload fields
   *   not part of quote identity (e.g. { price_source: 'stored' }) — merged
   *   in via buildQuotePayload's own `extra` param, same shape callers already
   *   pass to buildQuotePayload directly. Identity comparison (quoteIdentityEquals)
   *   is unaffected — price_source is a display/source selector, not a
   *   pricing input the Engine recomputes differently *because* the inputs
   *   changed.
   */
  request(identity, extra = {}) {
    if (this._timer) clearTimeout(this._timer);

    const mySeq = ++this._seq;

    return new Promise((resolve, reject) => {
      this._timer = setTimeout(() => {
        this._post(buildQuotePayload(identity, extra))
          .then((response) => {
            if (mySeq !== this._seq) return; // stale — молча игнорируем
            resolve(response);
          })
          .catch((error) => {
            if (mySeq !== this._seq) return; // stale — молча игнорируем
            reject(error);
          });
      }, this._debounceMs);
    });
  }

  /** Отправить немедленно (без debounce) — например, для ручного retry. */
  requestNow(identity, extra = {}) {
    if (this._timer) clearTimeout(this._timer);
    const mySeq = ++this._seq;

    return new Promise((resolve, reject) => {
      this._post(buildQuotePayload(identity, extra))
        .then((response) => {
          if (mySeq !== this._seq) return;
          resolve(response);
        })
        .catch((error) => {
          if (mySeq !== this._seq) return;
          reject(error);
        });
    });
  }

  /** Инвалидировать текущий in-flight запрос без отправки нового (например, unmount). */
  cancel() {
    if (this._timer) clearTimeout(this._timer);
    this._seq += 1;
  }
}

// Часть payload'а editSave, отвечающая за цены (CTR-03). Вынесена сюда, потому
// что это решение из трёх исходов, которое нельзя проверить, пока оно живёт
// внутри axios-вызова:
//   MANUAL  — оператор правит суммы: шлём manual_price и всю ручную панель;
//   ABSENT  — движку эту бронь пересчитывать нельзя: не шлём про суммы ничего;
//   RESET   — обычная бронь в авто: manual_price: null, сервер пересчитывает.
//
// ABSENT покрывает не только frozen. Бронь Localrent приходит к нам готовой, и
// поток односторонний: отмена тоже приезжает от них, а наших обновлений они не
// принимают (факт USER 2026-08-11). Пересчёт нашим движком разошёлся бы с тем,
// о чём клиент договорился у них, и согласовать это было бы нечем. То же для
// only_manual_editing (сервер 422-ит авто-сброс) и для архивной брони: она
// завершена, её суммы менять не за чем.
export function buildManualPricePart({ manualEditing, enginePricingForbidden, booking }) {
  if (manualEditing) {
    const part = {
      days: booking.days,
      hourly_hours: booking.hourly_hours,
      weekly_weeks: booking.weekly_weeks,
      monthly_months: booking.monthly_months,
      additional_hours: booking.additional_hours,
      hours_cost: booking.hours_cost,
      rental_cost: booking.rental_cost,
      total: booking.total,
    };
    // manual_price кладём ТОЛЬКО когда есть что положить. null по контракту
    // CTR-03 — не «цены нет», а явный СБРОС: сервер снимает manual_editing и
    // переписывает price/rental_cost/hours_cost расчётными. У легаси-брони с
    // NULL в колонке price (float, nullable, normalizes её не покрывает)
    // selected_price приезжает пустым, и сохранение из ручного режима молча
    // выбило бы бронь из него вместе с суммами — класс «деньгам доверили
    // null/пустую строку», прецедент 2026-07-21. Отсутствие ключа = absent:
    // сервер режим и суммы не трогает, остальные overrides панели (юниты,
    // rental_cost, total) применяются как обычно.
    if (booking.selected_price !== null && booking.selected_price !== undefined && booking.selected_price !== "") {
      part.manual_price = booking.selected_price;
    }
    return part;
  }
  if (enginePricingForbidden) return {};
  return { manual_price: null };
}

// FT-130: единый маппинг 422-кода квоты/persist в quoteStatus карточки.
// Известные коды переходят в одноимённый статус (шаблон рисует свой alert,
// pricingReady блокирует Save); всё прочее — generic "error".
const QUOTE_ERROR_CODES = [
  "no_price",
  "external_calculation",
  "prolongs_base_degenerate",
  "prolongs_lock_dates",
  "prolongs_frozen_pricing",
];

export function quoteErrorStatus(error) {
  const resp = error && error.response;
  const code = resp && resp.data && resp.data.error && resp.data.error.code;
  if (resp && resp.status === 422 && QUOTE_ERROR_CODES.includes(code)) return code;
  return "error";
}

// Санитарный потолок юнитов ОДНОГО продления — зеркало серверного
// ProlongUnitsLimit::MAX_PROLONG_UNITS (rentalman-api). Держится ради опечатки
// «цена в поле количества»: в проде так появились сегменты на 30994 суток, а
// бронь с таким сегментом навсегда уходила в вырожденный класс — любая
// последующая правка дат отвечала 422 prolongs_base_degenerate.
//
// По часам потолок не годовой (8760 ч), а 31 сутки: почасовые брони по факту
// часовые, и цена, набранная в поле часов (1500–3000), при годовом потолке
// прошла бы насквозь. Значения меняются ТОЛЬКО парой с сервером.
export const MAX_PROLONG_UNITS = { daily: 366, hourly: 744, weekly: 52, monthly: 12 };

export function prolongUnitsOverLimit(rentalType, units) {
  const limit = MAX_PROLONG_UNITS[rentalType];
  if (!limit) return false;
  return Number(units) > limit;
}

// FT-166: отказ 422 no_price означает ровно одно — у машины не заполнены цены
// под ВЫБРАННЫЙ тип аренды. Даты причиной не бывают: Pricing::Engine при
// отсутствии сезонной строки откатывается на базовую (engine.rb:318), и
// NoPriceError летит, только когда пусты обе. Кросс-типового фолбэка
// (hourly → daily) нет намеренно — «тихий 0 запрещён».
//
// Ключи разведены по типам аренды, а не собраны подстановкой {type} в один:
// в 16 локалях склонение названия типа внутри фразы даёт кривые формы.
// weekly/monthly ведут в общий фолбэк — отказы по ним единичны.
// Object.create(null), а не литерал: у литерала NO_PRICE_CAUSE_KEYS["constructor"]
// вернул бы функцию из прототипа — truthy, и в $t() уехала бы функция вместо ключа.
// Сейчас недостижимо (currentRentalType отдаёт 4 литерала), но цена защиты нулевая.
const NO_PRICE_CAUSE_KEYS = Object.assign(Object.create(null), {
  hourly: "bookings.quote_no_price_hourly",
  daily: "bookings.quote_no_price_daily",
});

export function noPriceCauseKey(rentalType) {
  return NO_PRICE_CAUSE_KEYS[rentalType] || "bookings.quote_no_price";
}

// Списки прайс-строк по типу аренды — ровно те коллекции, что отдаёт
// cars#show_for_new_booking (as_json include: prices/hourly_prices/
// weekly_prices/monthly_prices).
const PRICE_COLLECTIONS = Object.assign(Object.create(null), {
  daily: "prices",
  hourly: "hourly_prices",
  weekly: "weekly_prices",
  monthly: "monthly_prices",
});

export function pricesForRentalType(car, rentalType) {
  const key = PRICE_COLLECTIONS[rentalType];
  if (!car || !key) return [];
  return car[key] || [];
}

/**
 * Заполнена ли БАЗОВАЯ прайс-строка (season_id === null) хотя бы одной ценой.
 *
 * НАМЕРЕННО СТРОЖЕ СЕРВЕРА, и это не описка. Сервер падает на `values.empty?`
 * (engine.rb:318), то есть строку `[0, 0]` считает пригодной и выдаёт квоту по
 * цене 0. Здесь такая строка считается НЕзаполненной: цены, выставленные в
 * ноль, — это незаполненные цены, а сам движок в engine.rb:557 объявляет
 * «тихий 0 запрещён». Ослабить до `rows.length > 0` нельзя — вернётся ровно та
 * дыра, которую фича закрывает.
 *
 * Расхождение односторонне и безопасно: любое значение > 0 делает массив
 * непустым, поэтому пропустить настоящий 422 предикат не может — он может
 * только предупредить там, где сервер выдал бы ноль. Случай `[null, null]`
 * у сервера вообще не 422, а 500 (BigDecimal("") → ArgumentError), и
 * предупредить о нём тем более верно.
 *
 * Именно здесь фронт и сервер раньше расходились: фронт проверял НАЛИЧИЕ
 * строки (`prices.length > 0 && some(season_id === null)`), сервер — пустоту
 * values. Машина с базовой строкой и пустым values проходила проверку молча
 * и доходила до 422 — так вёл себя целый класс машин компании 11747
 * (health-check 21.09.2026, 128 отказов по почасовой за сутки).
 *
 * Частично заполненная строка считается заполненной: движок в этом случае
 * берёт values.last и отказа не даёт, предупреждать не о чем.
 */
export function hasUsableBasePrices(rows) {
  if (!Array.isArray(rows)) return false;
  // При дублях базовых строк берём ПОСЛЕДНЮЮ — так же, как сервер: PriceSet
  // кладёт строки в хеш по season_id, отсортировав по id, поэтому побеждает
  // последняя. `find` брал бы первую и на паре «дубль с ценами + дубль с
  // пустым values» разошёлся бы с движком (дубли в этих данных реальны —
  // из-за них цена машины когда-то «прыгала», см. комментарий в price_set.rb).
  let base = null;
  for (const row of rows) {
    if (row && row.season_id === null) base = row;
  }
  if (!base) return false;
  return Object.values(base.values || {}).some((value) => Number(value) > 0);
}
